import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, ArrowRight, Loader2, CheckCircle2, Sparkles, Home } from "lucide-react";
import { api } from "../lib/api";
import Navbar from "./Navbar";
import Footer from "./Footer";
import Seo from "./Seo";
import { courseUrl } from "../mock";

const QUESTIONS = [
  {
    id: "situation",
    q: "Qual è la tua situazione digitale attuale?",
    options: [
      { label: "Nessuna presenza online strutturata", scores: { "controlla-la-tua-comunicazione-digitale": 3, "social-media-marketing": 2 } },
      { label: "Gestisco i social ma senza strategia", scores: { "social-media-marketing": 3, "controlla-la-tua-comunicazione-digitale": 2 } },
      { label: "Faccio già campagne pubblicitarie", scores: { "facebook-advanced": 3, "customer-loyalty": 1 } },
      { label: "Ho un e-commerce/CRM con clienti", scores: { "customer-loyalty": 3, "agenti-ai": 2 } },
      { label: "Voglio integrare l'AI nel mio business", scores: { "agenti-ai": 3, "facebook-advanced": 1 } },
    ],
  },
  {
    id: "goal",
    q: "Il tuo obiettivo prioritario ora è...",
    options: [
      { label: "Farmi conoscere e aumentare la visibilità", scores: { "social-media-marketing": 3, "controlla-la-tua-comunicazione-digitale": 2 } },
      { label: "Acquisire nuovi clienti online", scores: { "facebook-advanced": 3, "controlla-la-tua-comunicazione-digitale": 2 } },
      { label: "Massimizzare il valore dei clienti esistenti", scores: { "customer-loyalty": 3, "agenti-ai": 1 } },
      { label: "Automatizzare processi ripetitivi", scores: { "agenti-ai": 3, "customer-loyalty": 2 } },
      { label: "Avere un sistema completo e replicabile", scores: { "controlla-la-tua-comunicazione-digitale": 3, "customer-loyalty": 1, "facebook-advanced": 1 } },
    ],
  },
  {
    id: "skills",
    q: "Come descriveresti le tue competenze digitali?",
    options: [
      { label: "Zero, parto da zero", scores: { "controlla-la-tua-comunicazione-digitale": 3, "social-media-marketing": 2 } },
      { label: "Base — conosco i social", scores: { "social-media-marketing": 2, "customer-loyalty": 1 } },
      { label: "Medio — ho già toccato Meta Ads", scores: { "facebook-advanced": 3, "customer-loyalty": 1 } },
      { label: "Alto — conosco strumenti e piattaforme", scores: { "customer-loyalty": 2, "agenti-ai": 3, "facebook-advanced": 1 } },
    ],
  },
  {
    id: "budget",
    q: "Budget marketing mensile disponibile?",
    options: [
      { label: "Meno di 500 €", scores: { "social-media-marketing": 2, "controlla-la-tua-comunicazione-digitale": 3 } },
      { label: "500 – 2.000 €", scores: { "social-media-marketing": 2, "facebook-advanced": 2 } },
      { label: "2.000 – 5.000 €", scores: { "facebook-advanced": 3, "customer-loyalty": 2 } },
      { label: "Oltre 5.000 €", scores: { "facebook-advanced": 2, "customer-loyalty": 3, "agenti-ai": 2 } },
    ],
  },
  {
    id: "time",
    q: "Quanto tempo puoi dedicare al marketing digitale?",
    options: [
      { label: "Poco: cerco automazione max", scores: { "agenti-ai": 3, "customer-loyalty": 2 } },
      { label: "Medio: qualche ora a settimana", scores: { "social-media-marketing": 2, "facebook-advanced": 2, "customer-loyalty": 2 } },
      { label: "Molto: sono focalizzato sulla crescita", scores: { "controlla-la-tua-comunicazione-digitale": 3, "facebook-advanced": 2 } },
    ],
  },
];

export default function QuizPage({ onOpenBooking }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);  // 0..QUESTIONS.length-1 = questions, then "form", then "result"
  const [phase, setPhase] = useState("questions");
  const [answers, setAnswers] = useState({});
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const totalSteps = QUESTIONS.length;
  const progress = phase === "questions" ? ((step + 1) / (totalSteps + 1)) * 100 : phase === "form" ? 100 : 100;

  const handleAnswer = (option) => {
    const q = QUESTIONS[step];
    setAnswers({ ...answers, [q.id]: option });
    if (step < QUESTIONS.length - 1) {
      setStep(step + 1);
    } else {
      setPhase("form");
    }
  };

  const submit = async () => {
    const phoneValid = form.phone.trim().replace(/\D/g, "").length >= 8;
    if (!form.name || !form.email) return;
    if (!phoneValid) {
      setError("Inserisci un numero di cellulare valido: ti ricontatteremo per la chiamata di orientamento.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const payloadAnswers = Object.entries(answers).map(([qid, opt]) => {
        const question = QUESTIONS.find((q) => q.id === qid);
        return {
          question_id: qid,
          scores: opt.scores,
          question: question?.q || qid,
          answer: opt.label,
        };
      });
      const res = await api.post("/quiz/submit", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        answers: payloadAnswers,
        marketing_consent: true,
      });
      setResult(res.data);
      // GA4 quiz_completed event (prima del redirect)
      try {
        const { trackQuizCompleted } = await import("../lib/analytics");
        trackQuizCompleted({
          recommendedCourse: res.data.recommended_course,
          isAllIn: res.data.is_all_in,
          eventId: res.data.event_id,
        });
      } catch (_) { /* analytics failure never breaks flow */ }
      // Redirect alla landing dedicata con Cal.com embed
      const qs = new URLSearchParams({
        course: res.data.recommended_course || "",
        name: form.name.trim(),
        email: form.email.trim(),
      });
      if (res.data.is_all_in) qs.set("allin", "1");
      navigate(`/quiz/prenota-call?${qs.toString()}`);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  const currentQ = QUESTIONS[step];

  return (
    <div className="App grain min-h-screen">
      <Seo
        title="Quiz gratuito — Quale corso digitale fa per te? | MyDigitalAction"
        description="Rispondi a poche domande e scopri in 1 minuto qual è il percorso di formazione digitale più adatto a te. Quiz gratuito MyDigitalAction."
        path="/quiz"
      />
      <Navbar onOpenBooking={onOpenBooking} />

      <section className="pt-32 lg:pt-40 pb-16 lg:pb-24 relative overflow-hidden">
        <div className="absolute inset-0 gradient-glow pointer-events-none opacity-40" />

        <div className="container-max max-w-3xl relative">
          {/* Progress bar */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-3">
              <span className="section-label">Quiz orientativo</span>
              <span className="font-mono-label text-white/40 text-[10px]">
                {phase === "questions" ? `${step + 1} / ${totalSteps + 1}` : phase === "form" ? `${totalSteps + 1} / ${totalSteps + 1}` : "Completato"}
              </span>
            </div>
            <div className="h-1 bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full transition-all duration-500"
                style={{ width: `${progress}%`, background: "#d4ff3d" }}
              />
            </div>
          </div>

          {/* Header */}
          {phase !== "result" && (
            <div className="mb-10">
              <h1 className="font-display text-[32px] sm:text-[42px] lg:text-[54px] leading-[1.05] font-bold text-white">
                Quale corso<br />
                <span className="italic font-light text-white/60">fa davvero per te?</span>
              </h1>
              <p className="text-white/60 mt-4 text-base lg:text-lg max-w-xl">
                {phase === "questions"
                  ? "Rispondi a 5 domande veloci: in 2 minuti ti diciamo quale percorso è più adatto alla tua situazione."
                  : "Ultimo passo: dove ti mandiamo il risultato personalizzato?"}
              </p>
            </div>
          )}

          {/* Questions phase */}
          {phase === "questions" && currentQ && (
            <div>
              <div className="bg-card border border-white/10 rounded-2xl p-6 lg:p-8">
                <div className="font-mono-label text-white/40 text-[10px] mb-4">Domanda {step + 1}</div>
                <h2 className="font-display text-white text-2xl lg:text-3xl leading-tight">{currentQ.q}</h2>

                <div className="mt-6 space-y-2">
                  {currentQ.options.map((opt, i) => (
                    <button
                      key={i}
                      onClick={() => handleAnswer(opt)}
                      className="w-full text-left group bg-[#141414] hover:bg-white/5 border border-white/10 hover:border-white/30 rounded-xl px-5 py-4 transition-all flex items-center justify-between gap-4"
                    >
                      <span className="text-white text-sm lg:text-base">{opt.label}</span>
                      <ArrowRight size={16} className="text-white/30 group-hover:text-white transition-colors flex-shrink-0" />
                    </button>
                  ))}
                </div>
              </div>

              {step > 0 && (
                <button
                  onClick={() => setStep(step - 1)}
                  className="mt-6 text-white/50 hover:text-white text-sm inline-flex items-center gap-2"
                >
                  <ArrowLeft size={14} /> Indietro
                </button>
              )}
            </div>
          )}

          {/* Form phase */}
          {phase === "form" && (
            <div>
              <div className="bg-card border border-white/10 rounded-2xl p-6 lg:p-8">
                <div className="font-mono-label text-white/40 text-[10px] mb-4">Ultimo passo</div>
                <h2 className="font-display text-white text-2xl lg:text-3xl leading-tight">
                  Ci siamo. Stiamo elaborando il tuo profilo.
                </h2>
                <p className="text-white/70 text-sm mt-3">
                  Dove ti mandiamo il risultato personalizzato + il link per prenotare la <span className="text-white">chiamata di orientamento gratuita con Frank</span>?
                </p>

                <div
                  className="mt-5 rounded-xl border p-4 flex gap-3 items-start"
                  style={{ borderColor: "rgba(212,255,61,0.3)", background: "rgba(212,255,61,0.05)" }}
                  data-testid="quiz-call-bonus"
                >
                  <div className="text-2xl leading-none">🎁</div>
                  <div className="text-white/80 text-sm leading-relaxed">
                    <span className="text-white font-medium">In bonus con il tuo risultato:</span> 30 minuti di chiamata di orientamento gratuita con Frank per commentare insieme il tuo profilo e capire il percorso migliore per te.
                  </div>
                </div>

                <div className="mt-6 space-y-3">
                  <input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Nome e cognome *"
                    className="w-full bg-[#141414] border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-white/30"
                  />
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="Email *"
                    className="w-full bg-[#141414] border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-white/30"
                  />
                  <input
                    type="tel"
                    inputMode="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="Cellulare * (ti ricontattiamo per la call)"
                    data-testid="quiz-form-phone-input"
                    className="w-full bg-[#141414] border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-white/30"
                  />
                </div>

                {error && (
                  <div className="mt-4 p-3 rounded-lg border border-red-400/30 bg-red-500/10 text-red-300 text-xs">
                    {error}
                  </div>
                )}

                <button
                  onClick={submit}
                  disabled={loading || !form.name || !form.email || form.phone.trim().replace(/\D/g, "").length < 8}
                  data-testid="quiz-form-submit-btn"
                  className={`btn-primary mt-6 w-full justify-center ${(loading || !form.name || !form.email || form.phone.trim().replace(/\D/g, "").length < 8) ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <>Vedi il tuo corso <ArrowUpRight size={16} /></>}
                </button>

                <p className="text-white/40 text-[11px] mt-4 text-center">
                  Inviando il modulo accetti la privacy policy. Puoi cancellarti in qualsiasi momento.
                </p>
              </div>

              <button
                onClick={() => setPhase("questions")}
                className="mt-6 text-white/50 hover:text-white text-sm inline-flex items-center gap-2"
              >
                <ArrowLeft size={14} /> Torna alle domande
              </button>
            </div>
          )}

          {/* Result phase */}
          {phase === "result" && result && (
            <div>
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-6" style={{ background: "#d4ff3d", boxShadow: "0 0 40px rgba(212,255,61,0.3)" }}>
                  <CheckCircle2 size={36} className="text-black" strokeWidth={2.5} />
                </div>
                <span className="section-label">Il tuo risultato</span>
                <h1 className="font-display text-3xl lg:text-5xl text-white mt-6 leading-tight">
                  {form.name.split(" ")[0]}, ecco cosa consigliamo a te:
                </h1>
              </div>

              <div className="bg-card border border-white/10 rounded-2xl p-6 lg:p-10 relative overflow-hidden">
                <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full opacity-10 blur-3xl" style={{ background: "#3b5bff" }} />
                <div className="relative">
                  <div className="inline-flex items-center gap-2 border border-white/10 rounded-full px-3 py-1 text-[10px] uppercase tracking-widest text-white/70">
                    <Sparkles size={11} style={{ color: "#d4ff3d" }} /> Consigliato per te
                  </div>
                  <h2 className="font-display text-white text-2xl lg:text-4xl mt-4 leading-tight">
                    {result.recommended_name}
                  </h2>
                  <p className="text-white/60 mt-4 text-sm lg:text-base leading-relaxed">
                    Basandoci sulle tue risposte su situazione, obiettivi, competenze, budget e tempo, questo è il percorso che meglio si adatta al tuo momento imprenditoriale.
                  </p>

                  <div className="mt-8 flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={() => navigate(result.is_all_in ? "/#prezzo" : courseUrl(result.recommended_course))}
                      className="btn-primary"
                    >
                      Scopri il corso <ArrowUpRight size={16} />
                    </button>
                    <button onClick={onOpenBooking} className="btn-secondary">
                      Parla con un ns consulente
                    </button>
                  </div>
                </div>
              </div>

              <p className="text-white/40 text-xs text-center mt-6">
                Abbiamo inviato il dettaglio anche a <span className="text-white/70">{form.email}</span>. Controlla la casella.
              </p>

              <Link to="/" className="mt-8 inline-flex items-center gap-2 text-white/50 hover:text-white text-sm">
                <Home size={14} /> Torna alla home
              </Link>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
